// Scheduler — in-flight / concurrency / scheduled-run-tracking state (ADR-0011 PR-6b).
//
// The module-level maps + the per-card delivery claim/dedupe helpers. Shared by
// the execution and kanban-route layers and (for the cache/reaper) by
// manager-worker. closeScheduledRunsForCode closes runs via ./runs.js — the
// only sibling dependency (one-way state → runs).

import { loadSchedulerState } from '../../claude-scheduler.js';
import { finishRun } from './runs.js';


// Track in-flight Claude scheduled tasks and concurrency per agent
export const inFlightClaudeTasks = new Set<string>();
export const claudeTaskConcurrency = new Map<string, number>();
export const MAX_CLAUDE_CONCURRENCY = 2;


// In-memory scheduler states (loaded from disk on first access)
export const claudeSchedulerStates = new Map<string, ReturnType<typeof loadSchedulerState>>();


// codeName → (kanbanItemId → run_id) for scheduled-task runs opened on the
// in-session route. A session teardown closes these so an interrupted run
// doesn't hang open and mis-attribute the agent's later turns. Board-drain /
// card-done close (the normal happy-path close) lands in S2 (ENG-5600).
export const scheduledRunsByCode = new Map<string, Map<string, string>>();

// ENG-5601: cardId delivery dedupe across the poll path (reconcileScheduledRuns)
// and the realtime onCompletion handler, which can BOTH fire for the same done
// card. Two states, keyed on the cardId alone — cardIds are globally-unique
// UUIDs, so no per-agent bucket is needed, and that sidesteps the
// poll-vs-onCompletion keying mismatch (poll has code_name, onCompletion can
// fall back to the agent UUID when the agent isn't in the active set):
//   - `claimedScheduledCards`: a card someone is actively delivering (in-flight).
//   - `completedScheduledCards`: a card whose delivery RESOLVED (delivered or
//     terminally not-deliverable). The claim alone can't tell "in-flight" from
//     "done" — and conflating them lets the poll path close (and drop the retry
//     handle for) a run while the other path's delivery is still in flight and
//     might yet fail. So delivery returns a 4-state result and only a RESOLVED
//     state lets the caller close the run.
// Deliberately NOT cleared on session teardown: a done card lingers in the
// board's 24h done window and the run map (the retry handle) is dropped on
// close, so these must outlive both to stop a re-delivery within the process.
// (Cross-process restart can't be deduped without durable state — a known,
// narrow limitation: a human re-completing an already-delivered card after a
// manager restart could re-send. Bounded growth: one UUID per delivered card
// over manager uptime.)
export const claimedScheduledCards = new Set<string>();
export const completedScheduledCards = new Set<string>();

/** Outcome of a delivery attempt, telling the caller whether the run can close. */
export type ScheduledDeliveryResult =
  | 'delivered'  // we delivered it this call — caller may close the run
  | 'terminal'   // not deliverable (wrong status/source/template, no task) OR already
                 // delivered by the other path — caller may close the run
  | 'retry'      // transient failure (claim released) — caller MUST keep the run open
  | 'in_flight'; // another path holds the claim and hasn't resolved — caller must
                 // keep the run open; a later tick sees 'terminal' once it resolves

// Claim a card for delivery. Returns true the first time (caller should
// deliver), false if it's already claimed (in-flight or resolved).
export function claimScheduledCardDelivery(cardId: string): boolean {
  if (claimedScheduledCards.has(cardId)) return false;
  claimedScheduledCards.add(cardId);
  return true;
}

// Release a claim so a later attempt can retry — used when the delivery
// fetch/post throws BEFORE processClaudeTaskResult ran, and (ENG-6430) when
// processClaudeTaskResult itself reports a failed outcome so the result is
// re-delivered on a later poll rather than dropped.
export function releaseScheduledCardDelivery(cardId: string): void {
  claimedScheduledCards.delete(cardId);
}

// Mark a card's delivery as RESOLVED (delivered or terminal). The claim stays
// held; completion is what lets a concurrent caller's `in_flight` settle to
// `terminal` on its next look.
export function markScheduledCardDeliveryComplete(cardId: string): void {
  completedScheduledCards.add(cardId);
}

// Test-only: clear the delivery dedupe sets so a cardId constant reused across
// test cases doesn't leak a claim between them. No production caller.
export function __resetScheduledDeliveryDedupeForTest(): void {
  claimedScheduledCards.clear();
  completedScheduledCards.clear();
}

export function trackScheduledRun(codeName: string, cardId: string, runId: string): void {
  let m = scheduledRunsByCode.get(codeName);
  if (!m) {
    m = new Map();
    scheduledRunsByCode.set(codeName, m);
  }
  m.set(cardId, runId);
}

// True if this card was materialised by the in-session route
// (routeScheduledTaskViaKanban) and still has an open per-card run. cardIds are
// globally unique, so scanning every code's map is unambiguous — and it avoids
// depending on a resolved codeName (the realtime path can only fall back to the
// agent UUID when the agent isn't in the active set). Used to keep the
// onCompletion delivery off oneshot/fallback scheduled_task cards (those are
// materialised by executeAndProcessClaudeTask, which already self-delivers and
// never records the card here).
export function isScheduledCardTracked(cardId: string): boolean {
  for (const m of scheduledRunsByCode.values()) {
    if (m.has(cardId)) return true;
  }
  return false;
}

export function closeScheduledRunsForCode(
  codeName: string,
  outcome: 'completed' | 'failed' | 'cancelled' | 'timeout',
  reason: string,
): void {
  const m = scheduledRunsByCode.get(codeName);
  if (!m || m.size === 0) return;
  scheduledRunsByCode.delete(codeName);
  for (const runId of m.values()) {
    void finishRun(runId, outcome, { outcomeMessage: reason });
  }
}
